import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Shield, Save } from "lucide-react";

export default function AdminPlansTab() {
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState([]);
  const [plans, setPlans] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [approve, setApprove] = useState(null); // user being approved
  const [approveForm, setApproveForm] = useState({ plan: "growth", trial_days: 14 });
  const [saving, setSaving] = useState("");
  const [users, setUsers] = useState([]);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/admin/plan-features"), api.get("/admin/users")]).then(([r, u]) => {
      setFeatures(r.data.features); setPlans(r.data.plans); setMatrix(r.data.matrix);
      setUsers(u.data.users); setLoading(false);
    }).catch(() => { toast.error("Admin access required."); setLoading(false); });
  };
  useEffect(load, []);

  const setUserStatus = async (u, status) => {
    try {
      await api.post(`/admin/users/${u.id}/status`, { status });
      load();
      toast.success(`${u.email} → ${status}`);
    } catch { toast.error("Could not update user."); }
  };

  const confirmApprove = async () => {
    try {
      await api.post(`/admin/users/${approve.id}/status`, { status: "trial", plan: approveForm.plan, trial_days: Number(approveForm.trial_days) || 14 });
      toast.success(`${approve.email} → ${approveForm.plan} trial (${approveForm.trial_days}d)`);
      setApprove(null);
      load();
    } catch { toast.error("Could not approve."); }
  };

  const toggle = (plan, fid) => {
    setMatrix((m) => {
      const set = new Set(m[plan] || []);
      set.has(fid) ? set.delete(fid) : set.add(fid);
      return { ...m, [plan]: [...set] };
    });
  };

  const save = async (plan) => {
    setSaving(plan);
    try {
      await api.put("/admin/plan-features", { plan, features: matrix[plan] || [] });
      toast.success(`${plan} plan updated`);
    } catch { toast.error("Could not save."); }
    setSaving("");
  };

  if (loading) return <Loading />;

  const STATUS_STYLE = {
    pending: "bg-[#F59E0B]/15 text-[#F59E0B]", active: "bg-[#10B981]/15 text-[#10B981]",
    trial: "bg-[#007AFF]/15 text-[#4da3ff]", suspended: "bg-[#EF4444]/15 text-[#EF4444]",
    expired: "bg-[#EF4444]/15 text-[#EF4444]",
  };

  return (
    <div className="space-y-6" data-testid="admin-plans-tab">
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5 text-[#007AFF]" />
        <div>
          <h2 className="font-head font-bold text-white text-lg">Admin</h2>
          <p className="text-xs text-[#666]">Approve trial accounts and control which features each plan unlocks.</p>
        </div>
      </div>

      {/* Users / approvals */}
      <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="admin-users">
        <div className="p-5 border-b border-[#1a1a1a]"><h3 className="font-head font-bold text-white">Users &amp; Trial Approvals</h3><p className="text-xs text-[#666]">Approve a pending sign-up to grant them a trial of the system.</p></div>
        <div className="overflow-x-auto max-h-[40vh]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0f0f0f]"><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
              <th className="px-5 py-3">User</th><th className="px-5 py-3">Plan</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Trial</th><th className="px-5 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-t border-[#141414]">
                  <td className="px-5 py-3"><div className="text-white">{u.name || "—"}</div><div className="text-xs text-[#666]">{u.email}{u.role === "admin" ? " · admin" : ""}</div></td>
                  <td className="px-5 py-3 text-[#A3A3A3] capitalize">{u.plan}</td>
                  <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLE[u.status] || "bg-[#333] text-[#A3A3A3]"}`} data-testid={`user-status-${u.email}`}>{u.status}</span></td>
                  <td className="px-5 py-3 text-xs text-[#A3A3A3] tnum">{u.status === "trial" && u.trial_days_left != null ? `${u.trial_days_left}d left` : "—"}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {u.role !== "admin" && (["pending", "suspended", "expired"].includes(u.status) ? (
                      <button data-testid={`approve-${u.email}`} onClick={() => { setApproveForm({ plan: "growth", trial_days: 14 }); setApprove(u); }} className="text-[#10B981] hover:text-[#34d399] text-xs font-semibold mr-3">Approve trial</button>
                    ) : (
                      <button data-testid={`suspend-${u.email}`} onClick={() => setUserStatus(u, "suspended")} className="text-[#A3A3A3] hover:text-[#EF4444] text-xs font-semibold">Suspend</button>
                    ))}
                  </td>
                </tr>
              ))}
              {!users.length && <tr><td colSpan={5} className="px-5 py-8 text-center text-[#666]">No users yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!approve} onOpenChange={(o) => { if (!o) setApprove(null); }}>
        <DialogContent className="bg-[#0d0d0d] border-[#262626] text-white" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Approve trial · {approve?.email}</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Pick which plan to trial and how long it runs. Access reverts to locked when the trial ends.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Trial plan</Label>
              <Select value={approveForm.plan} onValueChange={(v) => setApproveForm((f) => ({ ...f, plan: v }))}>
                <SelectTrigger data-testid="approve-plan" className="bg-[#111] border-[#262626]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111] border-[#262626] text-white">
                  <SelectItem value="growth">Growth</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Trial length (days)</Label>
              <Input data-testid="approve-days" type="number" min={1} max={365} value={approveForm.trial_days} onChange={(e) => setApproveForm((f) => ({ ...f, trial_days: e.target.value }))} className="bg-[#111] border-[#262626]" />
            </div>
          </div>
          <DialogFooter><Button data-testid="approve-confirm" onClick={confirmApprove} className="bg-[#10B981] hover:bg-[#0d9668] text-black">Grant trial</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-x-auto" data-testid="feature-matrix">
        <div className="p-5 border-b border-[#1a1a1a]"><h3 className="font-head font-bold text-white">Plans &amp; Features</h3><p className="text-xs text-[#666]">Toggle which features each plan unlocks — applies to all users on that plan instantly.</p></div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#666] text-xs uppercase tracking-wider border-b border-[#1a1a1a]">
              <th className="px-5 py-3">Feature</th>
              {plans.map((p) => <th key={p.id} className="px-5 py-3 text-center">{p.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {features.map((f) => (
              <tr key={f.id} className="border-t border-[#141414]">
                <td className="px-5 py-3 text-white">{f.label}</td>
                {plans.map((p) => {
                  const checked = (matrix[p.id] || []).includes(f.id);
                  return (
                    <td key={p.id} className="px-5 py-3 text-center">
                      <input
                        type="checkbox"
                        data-testid={`toggle-${p.id}-${f.id}`}
                        checked={checked}
                        onChange={() => toggle(p.id, f.id)}
                        className="h-4 w-4 accent-[#007AFF] cursor-pointer"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="border-t border-[#1a1a1a]">
              <td className="px-5 py-3"></td>
              {plans.map((p) => (
                <td key={p.id} className="px-5 py-3 text-center">
                  <Button data-testid={`save-plan-${p.id}`} size="sm" disabled={saving === p.id} onClick={() => save(p.id)}
                    className="bg-[#10B981] hover:bg-[#0d9668] text-black rounded-full h-8">
                    <Save className="h-3.5 w-3.5 mr-1" /> Save
                  </Button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
